/**
 * Mobile device detection utilities
 * Helps identify mobile devices and adjust cookie settings accordingly
 */

export interface DeviceInfo {
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  userAgent: string;
  platform: string;
}

/**
 * Detect if the current device is mobile, tablet, or desktop
 * @param userAgent - The user agent string (optional, defaults to navigator.userAgent)
 * @returns DeviceInfo object with device type information
 */
export function detectDevice(userAgent?: string): DeviceInfo {
  const ua = userAgent || (typeof window !== 'undefined' ? navigator.userAgent : '');
  
  const isMobile = /Mobile|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /Tablet|iPad/i.test(ua) && !isMobile;
  const isDesktop = !isMobile && !isTablet;
  
  const platform = ua.includes('Windows') ? 'Windows' :
                   ua.includes('Mac') ? 'macOS' :
                   ua.includes('Linux') ? 'Linux' :
                   ua.includes('Android') ? 'Android' :
                   ua.includes('iOS') ? 'iOS' : 'Unknown';

  return {
    isMobile,
    isTablet,
    isDesktop,
    userAgent: ua,
    platform
  };
}

/**
 * Check if a hostname is a local/private IP address
 * This is used to detect local development environments
 */
function isLocalHostname(hostname: string): boolean {
  if (!hostname) return false;

  // Extract hostname without port (the Host header carries it: "localhost:3100")
  const hostnameWithoutPort = hostname.split(':')[0];

  // Check for localhost variants
  if (hostnameWithoutPort === 'localhost' || hostnameWithoutPort === '127.0.0.1') {
    return true;
  }

  // Check for private IP ranges:
  // 192.168.x.x (most common local network)
  // 10.x.x.x (private network)
  // 172.16-31.x.x (private network)
  const privateIpRegex = /^192\.168\.\d+\.\d+$|^10\.\d+\.\d+\.\d+$|^172\.(1[6-9]|2[0-9]|3[0-1])\.\d+\.\d+$/;
  return privateIpRegex.test(hostnameWithoutPort);
}

/**
 * Get mobile-optimized cookie settings
 * @param isProduction - Whether the app is running in production (from NODE_ENV)
 * @param domain - The domain for the cookie (optional)
 * @param hostname - The hostname from the request (optional, for local detection)
 * @returns Cookie configuration optimized for mobile devices
 */
export function getMobileOptimizedCookieSettings(
  isProduction: boolean, 
  domain?: string,
  hostname?: string
) {
  // Check if we're in a local environment
  // Option 1: Explicit environment variable (most secure)
  const isLocalDev = process.env.NEXT_PUBLIC_LOCAL_DEV === 'true';
  
  // Option 2: Detect local hostname from request (server-side validation)
  const isLocal = isLocalDev || (hostname ? isLocalHostname(hostname) : false);
  
  // Determine if we should use secure cookies
  // Only use secure in production AND when NOT in a local environment
  const useSecure = isProduction && !isLocal;
  
  // Determine if we should set domain
  // Only set domain in production AND when NOT in a local environment AND domain is provided
  const shouldSetDomain = isProduction && !isLocal && domain;
  
  const baseSettings = {
    httpOnly: true,
    secure: useSecure,
    sameSite: 'lax' as const, // Better mobile compatibility than 'strict'
    path: '/',
    maxAge: 7 * 24 * 60 * 60, // 7 days in seconds
  };

  if (shouldSetDomain) {
    return {
      ...baseSettings,
      domain: domain
    };
  }

  return baseSettings;
}

/**
 * Cookie options for the Supabase session cookies (`sb-<ref>-auth-token`), the only
 * session of the web admin. Same domain rule as getMobileOptimizedCookieSettings:
 * in production they carry the apex domain, so a session opened on
 * app.orquesti.com also reaches the company subdomain the user is sent to after
 * signing in. Without it they were host-only and the subdomain ran as `anon`.
 * No httpOnly: the browser client reads them to refresh the session.
 * Plain module: used by the server, the browser and the Edge middleware.
 */
export function getSupabaseCookieOptions(hostname?: string) {
  const isProduction = process.env.NODE_ENV === 'production';
  const isLocal = process.env.NEXT_PUBLIC_LOCAL_DEV === 'true' || (hostname ? isLocalHostname(hostname) : false);
  const domain = process.env.NEXT_PUBLIC_DOMAIN;
  const shared = isProduction && !isLocal && !!domain && !!hostname && hostname.split(':')[0].endsWith(domain);

  return {
    path: '/',
    sameSite: 'lax' as const,
    secure: isProduction && !isLocal,
    ...(shared ? { domain } : {}),
  };
}

/**
 * Check if the current environment has cookie restrictions
 * @returns true if cookies might be restricted (e.g., in private browsing)
 */
export function hasCookieRestrictions(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    // Test if we can set a test cookie
    document.cookie = 'test_cookie=1; path=/';
    const canSetCookies = document.cookie.includes('test_cookie=1');
    
    // Clean up test cookie
    document.cookie = 'test_cookie=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    
    return !canSetCookies;
  } catch (error) {
    console.warn('Cookie test failed:', error);
    return true;
  }
}
